import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SummaryCard, SummaryCardAction } from '../../../utils/interfaces';

@Component({
  selector: 'acs-summary',
  templateUrl: './acs-summary.component.html',
  styleUrls: ['./acs-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AcsSummaryComponent implements OnInit {
  
  // Core Data
  @Input() summaryCards: SummaryCard[] = [];
  
  // Layout Configuration
  @Input() gridColumns: 'auto' | 2 | 3 | 4 = 'auto';
  @Input() cardSize: 'small' | 'medium' | 'large' | 'compact' = 'medium';
  @Input() spacing: 'compact' | 'normal' | 'spaced' = 'normal';
  
  // Visual Options
  @Input() variant: 'default' | 'borderless' = 'default';
  @Input() animated: boolean = false;
  @Input() interactive: boolean = false;
  
  // Accessibility
  @Input() ariaLabel: string = 'Summary cards';
  @Input() role: string = 'region';
  
  // Events
  @Output() cardClick = new EventEmitter<SummaryCard>();
  @Output() cardAction = new EventEmitter<{ card: SummaryCard; action: SummaryCardAction }>();

  // Expose Math for template
  Math = Math;

  // Computed Properties
  get containerClass(): string {
    const classes = ['acs-summary'];
    
    if (this.variant !== 'default') {
      classes.push(`acs-summary--${this.variant}`);
    }
    
    if (this.spacing !== 'normal') {
      classes.push(`acs-summary--${this.spacing}`);
    }
    
    if (this.animated) {
      classes.push('acs-summary--animated');
    }
    
    return classes.join(' ');
  }

  get gridClass(): string {
    const classes = ['acs-summary__grid'];
    
    // Grid columns
    if (this.gridColumns !== 'auto') {
      classes.push(`acs-summary__grid--fixed-${this.gridColumns}`);
    }
    
    // Card size affects grid
    if (this.cardSize !== 'medium') {
      classes.push(`acs-summary__grid--${this.cardSize}`);
    }
    
    return classes.join(' ');
  }

  ngOnInit(): void {
    // Validate input data
    this.validateSummaryCards();
    
    // Set interactive class on cards if needed
    if (this.interactive) {
      this.summaryCards = this.summaryCards.map(card => ({
        ...card,
        cssClass: `${card.cssClass || ''} acs-summary__card--interactive`.trim()
      }));
    }
  }

  /**
   * Handle card click events
   */
  onCardClick(card: SummaryCard): void {
    if (this.interactive || card.clickable) {
      this.cardClick.emit(card);
    }
  }

  /**
   * Handle card action button clicks
   */
  onCardAction(card: SummaryCard): void {
    if (card.action) {
      this.cardAction.emit({ card, action: card.action });
      
      // Execute action callback if provided
      if (card.action.callback) {
        card.action.callback(card);
      }
    }
  }

  /**
   * Get card CSS classes
   */
  getCardClasses(card: SummaryCard): string {
    const classes = ['acs-summary__card'];
    
    // Variant
    if (card.variant) {
      classes.push(`acs-summary__card--${card.variant}`);
    }
    
    // Size
    if (this.cardSize) {
      classes.push(`acs-summary__card--${this.cardSize}`);
    }
    
    // Interactive
    if (this.interactive || card.clickable) {
      classes.push('acs-summary__card--interactive');
    }
    
    // Custom CSS class
    if (card.cssClass) {
      classes.push(card.cssClass);
    }
    
    return classes.join(' ');
  }

  /**
   * Format card value for display
   */
  formatCardValue(card: SummaryCard): string {
    // Handle null/undefined values gracefully
    if (card.value === 'null' || card.value === null || card.value === undefined) {
      return '0'; // Em dash for no data
    }

    if (typeof card.value === 'string') {
      return card.value;
    }

    if (typeof card.value === 'number') {
      // Handle different formatting options
      if (card.format === 'currency') {
        return this.formatCurrency(card.value);
      } else if (card.format === 'percentage') {
        return `${card.value}%`;
      } else if (card.format === 'compact') {
        return this.formatCompactNumber(card.value);
      } else {
        return card.value.toLocaleString();
      }
    }

    return String(card.value);
  }

  /**
   * Format currency values
   */
  private formatCurrency(value: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Format large numbers in compact form (1.2K, 1.5M, etc.)
   */
  private formatCompactNumber(value: number): string {
    const suffixes = ['', 'K', 'M', 'B', 'T'];
    let suffixIndex = 0;
    let formattedValue = value;

    while (formattedValue >= 1000 && suffixIndex < suffixes.length - 1) {
      formattedValue /= 1000;
      suffixIndex++;
    }

    const rounded = Math.round(formattedValue * 10) / 10;
    return `${rounded}${suffixes[suffixIndex]}`;
  }

  /**
   * Validate summary cards data
   */
  private validateSummaryCards(): void {
    if (!Array.isArray(this.summaryCards)) {
      console.warn('AcsSummaryComponent: summaryCards should be an array');
      this.summaryCards = [];
      return;
    }

    // Validate each card
    this.summaryCards.forEach((card, index) => {
      if (!card.id) {
        console.warn(`AcsSummaryComponent: Card at index ${index} is missing required 'id' property`);
      }
      if (card.value === undefined || card.value === null) {
        console.warn(`AcsSummaryComponent: Card '${card.id}' is missing required 'value' property`);
      }
      if (!card.label) {
        console.warn(`AcsSummaryComponent: Card '${card.id}' is missing required 'label' property`);
      }
    });
  }

  /**
   * Get accessible description for a card
   */
  getCardDescription(card: SummaryCard): string {
    let description = `${card.label}: ${this.formatCardValue(card)}`;
    
    if (card.sublabel) {
      description += `. ${card.sublabel}`;
    }
    
    if (card.change !== undefined) {
      const changeType = card.change > 0 ? 'increased' : card.change < 0 ? 'decreased' : 'unchanged';
      description += `. ${changeType} by ${Math.abs(card.change)}${card.changeUnit || '%'}`;
    }
    
    if (card.progress !== undefined) {
      description += `. Progress: ${card.progress}%`;
    }
    
    return description;
  }

  /**
   * Track by function for ngFor performance
   */
  trackByCardId(index: number, card: SummaryCard): string {
    return card.id;
  }
}